import { Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { registerYupValidations } from '@woovi/utils';

import type { PaymentMethodSelectedCustomerRegister_charge$key } from './__generated__/PaymentMethodSelectedCustomerRegister_charge.graphql.ts';
import PaymentMethodPaymentDeadline from './PaymentMethodPaymentDeadline.tsx';
import PaymentMethodSelectedCustomerRegisterForm from './PaymentMethodSelectedCustomerRegisterForm.tsx';
import PaymentMethodSelectedCustomerRegisterFormFields from './PaymentMethodSelectedCustomerRegisterFormFields.tsx';
import PaymentMethodSelectedCustomerRegisterValue from './PaymentMethodSelectedCustomerRegisterValue.tsx';
import PaymentMethodTransactionDetails from './PaymentMethodTransactionDetails.tsx';
import PaymentLinkAdditionalInfo from '../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../paymentLink/PaymentLinkComment.tsx';

registerYupValidations();

type PaymentMethodSelectedCustomerRegisterProps = {
  charge: PaymentMethodSelectedCustomerRegister_charge$key;
};

const PaymentMethodSelectedCustomerRegister = (
  props: PaymentMethodSelectedCustomerRegisterProps,
): ReactNode => {
  const charge = useFragment<PaymentMethodSelectedCustomerRegister_charge$key>(
    graphql`
      fragment PaymentMethodSelectedCustomerRegister_charge on Charge {
        id
        customerRegisterFields
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentLinkComment_charge
        ...PaymentMethodPaymentDeadline_charge
        ...PaymentMethodTransactionDetails_charge
        ...PaymentLinkActiveEarnCashback_charge
        ...PaymentLinkActiveCashbackFidelityBalance_charge
        ...PaymentLinkActiveEarnCashbackFidelity_charge
        ...PaymentMethodSelectedCustomerRegisterValue_charge
        ...PaymentMethodSelectedCustomerRegisterForm_charge
        ...PaymentMethodSelectedCustomerRegisterFormFields_charge
      }
    `,
    props.charge,
  );

  const getCustomerRegisterFormFields = (): ReactNode => {
    if (!charge?.customerRegisterFields?.length) {
      return <PaymentMethodSelectedCustomerRegisterForm charge={charge} />;
    }

    return <PaymentMethodSelectedCustomerRegisterFormFields charge={charge} />;
  };

  return (
    <Stack sx={{ width: '100%', gap: 2 }}>
      <Stack>
        <PaymentMethodSelectedCustomerRegisterValue charge={charge} />
        <PaymentLinkComment charge={charge} />
      </Stack>
      <Stack>{getCustomerRegisterFormFields()}</Stack>
      <Stack sx={{ gap: 1 }}>
        <PaymentMethodPaymentDeadline charge={charge} />
        <PaymentMethodTransactionDetails charge={charge} />
        <PaymentLinkAdditionalInfo charge={charge} />
      </Stack>
    </Stack>
  );
};

export default PaymentMethodSelectedCustomerRegister;
